// Reusable dark-theme calendar popover picker.
// Drop-in replacement for <input type="date"> — never triggers the browser's
// native picker (which is inconsistent on iOS/Android/desktop) and never
// closes the wizard on outside drag-selection.
//
// Usage:
//   <CalendarPicker value={form.date} onChange={v => set('date', v)}
//     min="2026-01-01" max="2026-12-31" placeholder="Pick a date" />
//
// Emits ISO YYYY-MM-DD strings.

import { useEffect, useRef, useState } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW = ['S','M','T','W','T','F','S'];

const pad = (n) => String(n).padStart(2, '0');
const toIso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const parseIso = (s) => {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { y: +m[1], m: +m[2] - 1, d: +m[3] };
};
const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const firstDow = (y, m) => new Date(y, m, 1).getDay();
const displayFmt = (iso) => {
  const p = parseIso(iso);
  if (!p) return '';
  return `${MONTHS[p.m].slice(0, 3)} ${p.d}, ${p.y}`;
};

export default function CalendarPicker({ value, onChange, min, max, placeholder = 'Select date', testid, disabled }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const parsed = parseIso(value);
  const today = new Date();
  const [view, setView] = useState({
    y: parsed?.y ?? today.getFullYear(),
    m: parsed?.m ?? today.getMonth(),
  });

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (parsed) setView({ y: parsed.y, m: parsed.m });
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const minP = parseIso(min);
  const maxP = parseIso(max);
  const cmp = (a, b) => a.y !== b.y ? a.y - b.y : (a.m !== b.m ? a.m - b.m : a.d - b.d);
  const isDisabled = (y, m, d) => {
    if (minP && cmp({ y, m, d }, minP) < 0) return true;
    if (maxP && cmp({ y, m, d }, maxP) > 0) return true;
    return false;
  };

  const nextMonth = () => setView(v => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 });
  const prevMonth = () => setView(v => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 });

  const pick = (d) => {
    if (isDisabled(view.y, view.m, d)) return;
    onChange(toIso(view.y, view.m, d));
    setOpen(false);
  };

  const total = daysInMonth(view.y, view.m);
  const skip = firstDow(view.y, view.m);
  const cells = [];
  for (let i = 0; i < skip; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const isSelected = (d) => parsed && parsed.y === view.y && parsed.m === view.m && parsed.d === d;
  const isTodayCell = (d) => today.getFullYear() === view.y && today.getMonth() === view.m && today.getDate() === d;

  return (
    <div ref={rootRef} style={{ position: 'relative', width: '100%' }}>
      <button type="button" data-testid={testid} onClick={() => !disabled && setOpen(v => !v)} disabled={disabled}
        style={{
          width: '100%', padding: '10px 12px', paddingRight: 36, borderRadius: 8, background: '#0A0A0A',
          border: `1px solid ${open ? '#E63946' : '#2A2A2A'}`, color: parsed ? '#fff' : '#666',
          fontSize: 14, outline: 'none', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1, position: 'relative',
        }}>
        {parsed ? displayFmt(value) : placeholder}
        <CalendarIcon style={{ width: 16, height: 16, color: '#E63946', position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }} />
      </button>

      {open && (
        <div data-testid={testid ? `${testid}-popover` : undefined}
          style={{
            position: 'absolute', zIndex: 100, top: 'calc(100% + 6px)', left: 0,
            minWidth: 280, padding: 12, borderRadius: 12,
            background: '#141414', border: '1px solid #333',
            boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
          }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <button type="button" onClick={prevMonth} style={{ padding: 4, color: '#A3A3A3', background: 'none', border: 'none', cursor: 'pointer' }}>
              <ChevronLeft style={{ width: 18, height: 18 }} />
            </button>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select value={view.m} onChange={e => setView({ ...view, m: +e.target.value })}
                style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', padding: '4px 6px', borderRadius: 6, fontSize: 13 }}>
                {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <input type="number" value={view.y}
                onChange={e => setView({ ...view, y: Math.max(1970, Math.min(2999, +e.target.value || view.y)) })}
                style={{ width: 68, background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', padding: '4px 6px', borderRadius: 6, fontSize: 13 }} />
            </div>
            <button type="button" onClick={nextMonth} style={{ padding: 4, color: '#A3A3A3', background: 'none', border: 'none', cursor: 'pointer' }}>
              <ChevronRight style={{ width: 18, height: 18 }} />
            </button>
          </div>

          {/* DOW headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {DOW.map((d, i) => (
              <div key={i} style={{ fontSize: 10, textAlign: 'center', color: '#525252', textTransform: 'uppercase' }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((d, i) => {
              if (d === null) return <div key={i} />;
              const disabledCell = isDisabled(view.y, view.m, d);
              const selected = isSelected(d);
              const todayFlag = isTodayCell(d);
              return (
                <button key={i} type="button" onClick={() => pick(d)} disabled={disabledCell}
                  data-testid={testid ? `${testid}-day-${toIso(view.y, view.m, d)}` : undefined}
                  style={{
                    aspectRatio: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 6, fontSize: 13, fontWeight: selected ? 700 : 400,
                    background: selected ? '#E63946' : (todayFlag ? 'rgba(230,57,70,0.15)' : 'transparent'),
                    color: disabledCell ? '#333' : (selected ? '#fff' : (todayFlag ? '#E63946' : '#E5E5E5')),
                    border: 'none', cursor: disabledCell ? 'not-allowed' : 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { if (!selected && !disabledCell) e.currentTarget.style.background = '#222'; }}
                  onMouseLeave={e => { if (!selected && !disabledCell) e.currentTarget.style.background = todayFlag ? 'rgba(230,57,70,0.15)' : 'transparent'; }}>
                  {d}
                </button>
              );
            })}
          </div>

          {/* Footer clear */}
          {value && (
            <div style={{ borderTop: '1px solid #262626', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" onClick={() => { onChange(''); setOpen(false); }}
                style={{ fontSize: 11, color: '#737373', background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 1 }}>
                Clear
              </button>
              <button type="button" onClick={() => setOpen(false)}
                style={{ fontSize: 11, color: '#E63946', background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
